import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import {
  UserRole,
  UserRoleResponse,
  CreateUserRoleRequest,
  UpdateUserRoleRequest,
  PaginationResponse,
  ApiResponse,
} from '@magnetar/admin-api-interfaces'
import { buildRolesPath, rolesPathForOwner } from './rolesPath'
import type { RoleDeletionImpact } from '@/pages/userRoles/roleDeletion'

interface GetUserRolesParams {
  page?: number
  pageSize?: number
  scopeType?:
    | 'system'
    | 'sys_admin'
    | 'clinic'
    | 'supplier'
    | 'ad_supplier'
    | 'provider'
    | 'clinic_group'
    | 'ad_supplier_group'
  clinicId?: number
  supplierId?: number
  providerId?: number
  clinicGroupId?: number
  adSupplierGroupId?: number
}

function listParentId(params: GetUserRolesParams): number | null {
  switch (params.scopeType) {
    case 'provider':
      return params.providerId ?? null
    case 'clinic':
      return params.clinicId ?? null
    case 'ad_supplier':
    case 'supplier':
      return params.supplierId ?? null
    case 'clinic_group':
      return params.clinicGroupId ?? null
    case 'ad_supplier_group':
      return params.adSupplierGroupId ?? null
    default:
      return null
  }
}

export const userRoleService = {
  getUserRoles: async (
    params: GetUserRolesParams,
    token: string,
  ): Promise<ApiResponse<PaginationResponse<UserRole>>> => {
    try {
      const query = new URLSearchParams()
      if (params.page) query.append('page', params.page.toString())
      if (params.pageSize) query.append('pageSize', params.pageSize.toString())

      const base = buildRolesPath(params.scopeType, listParentId(params))
      const qs = query.toString()
      const url = qs ? `${base}?${qs}` : base

      const response = await apiClient.get<ApiResponse<PaginationResponse<UserRole>>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<PaginationResponse<UserRole>>
      }
      return {
        success: false,
        error: {
          code: 'GET_USER_ROLES_FAILED',
          message: '取得使用者角色列表失敗',
        },
      }
    }
  },

  updateUserRole: async (
    role: UserRole,
    data: UpdateUserRoleRequest,
    token: string,
  ): Promise<UserRoleResponse> => {
    try {
      const response = await apiClient.put<UserRoleResponse>(
        rolesPathForOwner(role, role.user_role_id),
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as UserRoleResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'UPDATE_USER_ROLE_FAILED',
          message: '更新使用者角色失敗',
        },
      }
    }
  },

  createUserRole: async (data: CreateUserRoleRequest, token: string): Promise<UserRoleResponse> => {
    try {
      const response = await apiClient.post<UserRoleResponse>(rolesPathForOwner(data), data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as UserRoleResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'CREATE_USER_ROLE_FAILED',
          message: '建立使用者角色失敗',
        },
      }
    }
  },

  // 預覽「刪除此角色」的影響（不寫入）：使用中的帳號(擋)、主檔過期指向可自動修復的帳號、無法修復的帳號(擋)。
  getRoleDeletionImpact: async (
    role: UserRole,
    token: string,
  ): Promise<{ success: boolean; data?: RoleDeletionImpact; error?: { code: string; message: string } }> => {
    try {
      const response = await apiClient.get<{ success: boolean; data: RoleDeletionImpact }>(
        `${rolesPathForOwner(role, role.user_role_id)}/deletion-impact`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data
      }
      return {
        success: false,
        error: { code: 'GET_ROLE_DELETION_IMPACT_FAILED', message: '無法取得刪除影響' },
      }
    }
  },

  deleteUserRole: async (role: UserRole, token: string): Promise<UserRoleResponse> => {
    try {
      const response = await apiClient.delete<UserRoleResponse>(
        rolesPathForOwner(role, role.user_role_id),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          // 後端在有「主檔角色過期指向」要自動修復時要求明確確認（400 CONFIRMATION_REQUIRED）；
          // Dialog 已先以 deletion-impact 讓使用者確認過，這裡帶上 confirm。
          data: { confirm: true },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as UserRoleResponse
      }
      return {
        success: false,
        data: null,
        error: {
          code: 'DELETE_USER_ROLE_FAILED',
          message: '刪除使用者角色失敗',
        },
      }
    }
  },
}
